import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../../lib/api";

/**
 * BrandSplash — full-screen intro that plays ONCE per browser session.
 * Shows animated Company Name + cycling taglines, then fades out to reveal
 * the main Home page and its hero carousel underneath.
 *
 * Trigger conditions:
 *  - Only renders on `/` (path passed via prop `enabled`)
 *  - Uses sessionStorage("gesil_splash_seen") to avoid re-showing on nav.
 */
const TAGLINES = [
  "Transforming Governance Through Technology",
  "Reimagining Public Service Delivery",
  "Sovereign AI · Government Grade · Secure",
];

const SPLASH_KEY = "gesil_splash_seen";

export default function BrandSplash({ enabled = true }) {
  const [visible, setVisible] = useState(false);
  const [companyName, setCompanyName] = useState("Global e-Village Services India Ltd");
  const [tagIdx, setTagIdx] = useState(0);

  // Decide whether to show splash (one time per session)
  useEffect(() => {
    if (!enabled) return;
    let seen = false;
    try { seen = sessionStorage.getItem(SPLASH_KEY) === "1"; } catch { /* ignore */ }
    if (seen) return;
    setVisible(true);
    try { sessionStorage.setItem(SPLASH_KEY, "1"); } catch { /* ignore */ }
  }, [enabled]);

  // Pull the latest company name from site-settings for consistency
  useEffect(() => {
    if (!visible) return;
    api.get("/site-settings")
      .then((r) => { if (r.data?.company_name) setCompanyName(r.data.company_name); })
      .catch(() => {});
  }, [visible]);

  // Cycle taglines every 900ms while visible
  useEffect(() => {
    if (!visible) return;
    const t = setInterval(() => setTagIdx((i) => (i + 1) % TAGLINES.length), 900);
    // Fade out after 3.2s total
    const fade = setTimeout(() => setVisible(false), 3200);
    return () => { clearInterval(t); clearTimeout(fade); };
  }, [visible]);

  // Allow skip on click / keydown
  useEffect(() => {
    if (!visible) return;
    const skip = () => setVisible(false);
    window.addEventListener("keydown", skip);
    return () => window.removeEventListener("keydown", skip);
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.03, filter: "blur(6px)" }}
          transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
          onClick={() => setVisible(false)}
          data-testid="brand-splash"
          className="fixed inset-0 z-[999] cursor-pointer flex items-center justify-center bg-gradient-to-br from-[#0B1B40] via-[#0F172A] to-[#1E3A8A] text-white overflow-hidden"
        >
          {/* Animated background blobs */}
          <div className="absolute -top-40 -left-40 h-[560px] w-[560px] rounded-full bg-blue-500/30 blur-3xl animate-pulse" />
          <div className="absolute -bottom-40 -right-40 h-[560px] w-[560px] rounded-full bg-cyan-400/25 blur-3xl animate-pulse" style={{ animationDelay: "0.7s" }} />

          {/* Grid overlay */}
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
              backgroundSize: "42px 42px",
            }}
          />

          <div className="relative text-center px-6 max-w-5xl">
            {/* Eyebrow */}
            <motion.div
              initial={{ opacity: 0, letterSpacing: "0.4em" }}
              animate={{ opacity: 1, letterSpacing: "0.28em" }}
              transition={{ duration: 0.7 }}
              className="text-[11px] md:text-xs uppercase text-cyan-300 font-semibold"
            >
              — Global e-Village Services India Ltd —
            </motion.div>

            {/* Company Name — word-by-word reveal */}
            <div className="mt-6 font-display text-4xl sm:text-6xl lg:text-7xl xl:text-8xl font-semibold leading-[1.05] tracking-tight">
              {companyName.split(" ").map((w, i) => (
                <motion.span
                  key={`${w}-${i}`}
                  initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{ duration: 0.7, delay: 0.15 + i * 0.09, ease: [0.22, 1, 0.36, 1] }}
                  className="inline-block whitespace-pre"
                >
                  {w + " "}
                </motion.span>
              ))}
            </div>

            {/* Animated tag underline */}
            <motion.div
              className="mx-auto mt-8 h-[2px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent"
              initial={{ width: 0 }}
              animate={{ width: "220px" }}
              transition={{ duration: 0.8, delay: 0.9 }}
            />

            {/* Cycling taglines */}
            <div className="mt-8 h-8 md:h-10 relative flex items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.p
                  key={tagIdx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.45 }}
                  className="font-display italic text-lg md:text-2xl text-blue-100"
                  data-testid={`splash-tag-${tagIdx}`}
                >
                  &ldquo;{TAGLINES[tagIdx]}&rdquo;
                </motion.p>
              </AnimatePresence>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.4, duration: 0.4 }}
              className="mt-14 text-[10px] uppercase tracking-[0.35em] text-blue-300"
            >
              Tap anywhere to enter →
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
